import React, { useState } from "react";
import * as O from "fp-ts/Option";
import * as RA from "fp-ts/ReadonlyArray";
import { useMutation, useQuery } from "@apollo/client";
import { ApolloError, gql } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { pipe } from "fp-ts/function";

import { Button } from "src/components/button/button";
import { Error } from "src/components/error/error";
import { Input } from "src/components/input/input";
import { Select } from "src/components/select/select";
import { SectionTitle } from "src/components/typography/typography";
import { School, SchoolConnection, SchoolEdge, UpdateSchoolInput, UpdateSchoolPayload } from "src/generated/types";
import { parseError } from "src/utils/parse_error";

import { AdminLayout } from "./admin_layout";
import { getSchoolChoices } from "./helpers";
import { ALL_SCHOOLS_QUERY } from "./queries";

/*
 * GraphQL
 */

export const UPDATE_SCHOOL_MUTATION = gql`
    mutation UpdateSchool($input: UpdateSchoolInput!) {
        updateSchool(input: $input) {
            school {
                id
                accountNumber
                address
                name
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloMutationVars {
    input: UpdateSchoolInput;
}

interface ApolloMutationData {
    updateSchool: UpdateSchoolPayload;
}

interface ApolloQueryData {
    schools: SchoolConnection;
}

interface FormValues {
    accountNumber: string;
    address: string;
    name: string;
}

const NO_SCHOOL = "*";

/*
 * Main
 */

export const EditSchoolPage = (): React.ReactElement => {
    const { data, error } = useQuery<ApolloQueryData, Empty>(ALL_SCHOOLS_QUERY, {
        fetchPolicy: "network-only",
    });
    const [updateSchool, { loading }] = useMutation<ApolloMutationData, ApolloMutationVars>(UPDATE_SCHOOL_MUTATION);
    const [schoolId, setSchoolId] = useState<string>(NO_SCHOOL);

    // Same cast the Create User / Create Report pages make: Parse never returns a null edge here.
    const edges = (data?.schools.edges ?? []) as ReadonlyArray<SchoolEdge>;

    const school: School | undefined = pipe(
        edges,
        RA.findFirst((edge) => edge.node?.id === schoolId),
        O.chain((edge) => O.fromNullable(edge.node)),
        O.toUndefined,
    );

    const initialValues: FormValues = {
        accountNumber: school?.accountNumber ?? "",
        address: school?.address ?? "",
        name: school?.name ?? "",
    };

    if (error) return <Error />;

    return (
        <AdminLayout>
            <SectionTitle>Edit a School</SectionTitle>
            <Select
                id="schoolId"
                label="School"
                onChange={(e): void => setSchoolId(e.target.value)}
                options={getSchoolChoices(edges)}
                value={schoolId}
            />
            {school && (
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={(values, { setSubmitting }): void => {
                        updateSchool({
                            variables: {
                                input: {
                                    id: school.id,
                                    fields: {
                                        accountNumber: values.accountNumber.trim(),
                                        address: values.address,
                                        name: values.name,
                                    },
                                },
                            },
                        })
                            .then(() => {
                                toast.success("School updated successfully");
                            })
                            .catch((e: ApolloError) => {
                                toast.error(parseError(e));
                            });

                        setSubmitting(false);
                    }}
                    validationSchema={Yup.object().shape({
                        accountNumber: Yup.string(),
                        address: Yup.string().required("Required"),
                        name: Yup.string().required("Required"),
                    })}
                >
                    {(formik: FormikProps<FormValues>) => (
                        <Form>
                            <Input
                                autoComplete="off"
                                id="name"
                                label="Name"
                                name="name"
                                placeholder="Organization Name"
                            />
                            <Input
                                autoComplete="off"
                                id="address"
                                label="Address"
                                name="address"
                                placeholder="Organization Address"
                            />
                            <Input
                                autoComplete="off"
                                id="accountNumber"
                                label="Account Number"
                                name="accountNumber"
                                placeholder="ACSI Account Number (optional)"
                            />
                            <Button disabled={formik.isSubmitting || !formik.isValid || loading} type="submit">
                                Save
                            </Button>
                        </Form>
                    )}
                </Formik>
            )}
        </AdminLayout>
    );
};
